import React, { Children, useEffect, useState } from "react";
import { Labeled, useInput, useTranslate } from "react-admin";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import { makeStyles } from "@material-ui/core/styles";
import FormHelperText from "@material-ui/core/FormHelperText";
import classnames from "classnames";
import { LinearProgressWithLabel } from "./Basic";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import OpenInBrowserIcon from "@material-ui/icons/OpenInBrowser";
import useUpload from "../utils/useUpload";
import { ADMIN_DOMAIN } from "../constants";

const useStyles = makeStyles(
  (theme) => ({
    dropZone: {
      background: theme.palette.background.default,
      cursor: 'pointer',
      padding: theme.spacing(1),
      textAlign: 'center',
      color: theme.palette.getContrastText(theme.palette.background.default),
    },
    preview: {},
    removeButton: {},
    root: { width: '100%' },
  }),
  { name: 'RaFileInput' }
);

export const RaFileInput = (props) => {
  const {
    accept,
    children,
    className,
    classes: classesOverride,
    format,
    helperText,
    label,
    labelMultiple = 'ra.input.file.upload_several',
    labelSingle = 'ra.input.file.upload_single',
    maxSize,
    minSize,
    multiple = false,
    options: { inputProps: inputPropsOptions, ...options } = {},
    parse,
    placeholder,
    resource,
    source,
    validate,
    uploadEndpoint = ADMIN_DOMAIN+"/admin/file",
    ...rest
  } = props;
  const translate = useTranslate();
  const classes = useStyles(props);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { progress, upload } = useUpload(uploadEndpoint ,options);

  useEffect(() => {
    setUploadProgress(progress);
  }, [progress]);
  // turn a browser dropped file structure into expected structure
  const transformFile = (file) => {
    if (!(file instanceof File)) {
      return file;
    }

    const { source, title } = Children.only(children).props;

    const preview = URL.createObjectURL(file);
    const transformedFile = {
      rawFile: file,
      [source]: preview,
    };

    if (title) {
      transformedFile[title] = file.name;
    }

    return transformedFile;
  };

  const transformFiles = (files) => {
    if (!files) {
      return multiple ? [] : null;
    }
    if (Array.isArray(files)) {
      return files.map(transformFile);
    }

    return transformFile(files);
  };

  const {
    id,
    input: { onChange, value, ...inputProps },
    meta,
    isRequired,
  } = useInput({
    format: format || transformFiles,
    parse: parse || transformFiles,
    source,
    type: 'file',
    validate,
    ...rest,
  });

  // const { touched, error } = meta;
  // if(value)
  //   value = value.result.url
  const files =  value?.result?.url ? (Array.isArray( value?.result?.url) ?  value?.result?.url : [ value?.result?.url]) : [];

  const onDrop = async (newFiles, rejectedFiles, event) => {
    const updatedFiles = multiple ? [...files, ...newFiles] : [...newFiles];

    if (multiple) {
      let uploadResult = await upload(updatedFiles);
      setUploadProgress(100);
      onChange(uploadResult);
    } else {
      let uploadResult = await upload(updatedFiles[0]);
      setUploadProgress(100);
      onChange(uploadResult);
    }

    if (options.onDrop) {
      options.onDrop(newFiles, rejectedFiles, event);
    }
  };

  // const onRemove = (file) => () => {
  //   if (multiple) {
  //     const filteredFiles = files.filter((stateFile) => !shallowEqual(stateFile, file));
  //     onChange(filteredFiles);
  //   } else {
  //     onChange('null');
  //   }
  //
  //   if (options.onRemove) {
  //     options.onRemove(file);
  //   }
  // };

  // const childrenElement = children && isValidElement(Children.only(children)) ? Children.only(children) : undefined;

  const { getRootProps, getInputProps } = useDropzone({
    ...options,
    accept,
    maxSize,
    minSize,
    multiple,
    onDrop,
  });

  return (
    <>
      <Labeled
        id={id}
        label={label}
        className={classnames(classes.root, className)}
        source={source}
        resource={resource}
        isRequired={isRequired}
        meta={meta}
        {...rest}
      >
        <>
          <div data-testid="dropzone" className={classes.dropZone} {...getRootProps()}>
            <input
              id={id}
              {...getInputProps({
                ...inputProps,
                ...inputPropsOptions,
              })}
            />
            {placeholder ? placeholder : multiple ? <p>{translate(labelMultiple)}</p> : <p>{translate(labelSingle)}</p>}
          </div>
          <div className="previews">
            <FormHelperText>
              {/*<InputHelperText touched={touched} error={error} helperText={helperText} />*/}
              {helperText}
            </FormHelperText>
            {files.map((file, index) => {
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    margin: '1em 0',
                    backgroundColor: '#eee',
                    padding: '0.5em',
                  }}
                >
                  <Link aria-label="open" color="primary" component={'a'} href={file} target={'_blank'}>
                    <OpenInBrowserIcon />
                  </Link>
                  <Typography variant="caption">{file}</Typography>
                </div>
              );
            })}
            <LinearProgressWithLabel value={uploadProgress} />
          </div>
        </>
      </Labeled>
    </>
  );
};

RaFileInput.propTypes = {
  accept: PropTypes.string,
  children: PropTypes.element,
  classes: PropTypes.object,
  className: PropTypes.string,
  id: PropTypes.string,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  labelMultiple: PropTypes.string,
  labelSingle: PropTypes.string,
  maxSize: PropTypes.number,
  minSize: PropTypes.number,
  multiple: PropTypes.bool,
  options: PropTypes.object,
  resource: PropTypes.string,
  source: PropTypes.string,
  placeholder: PropTypes.node,
  uploadEndpoint: PropTypes.string,
};
